import {
  computed,
  onScopeDispose,
  ref,
  toValue,
  watch,
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref,
} from 'vue'

import { insightWorkbenchApi } from '../api/InsightWorkbenchApi'
import type { InsightWorkbenchConfigParams } from '../models/insightApi.types'
import type { InsightConfig } from '../models/insightConfig.types'
import type { WorkbenchAsyncState } from '../models/workbench.types'
import { normalizeWorkbenchPlatformFilter } from '../service/insightWorkbench'

import { ApiError, type Paginated } from '@/shared/http'

export interface InsightConfigBrowserFilters {
  search: string
  status: string
  platform: string
  outcome: string
  severity: string
}

interface InsightConfigBrowserApiPort {
  listConfigs: (
    accountId: number,
    workspaceId: number,
    params?: InsightWorkbenchConfigParams,
    signal?: AbortSignal,
  ) => Promise<Paginated<InsightConfig>>
}

export interface UseInsightConfigBrowserOptions {
  accountId: MaybeRefOrGetter<number | undefined>
  workspaceId: MaybeRefOrGetter<number | undefined>
  enabled?: MaybeRefOrGetter<boolean>
  api?: InsightConfigBrowserApiPort
  limit?: number
}

export interface UseInsightConfigBrowser {
  configs: ComputedRef<readonly InsightConfig[]>
  total: ComputedRef<number>
  offset: ComputedRef<number>
  state: ComputedRef<WorkbenchAsyncState>
  error: ComputedRef<ApiError | null>
  draftFilters: Ref<InsightConfigBrowserFilters>
  appliedFilters: ComputedRef<InsightConfigBrowserFilters>
  applyFilters: () => Promise<void>
  clearFilters: () => Promise<void>
  load: () => Promise<void>
  nextPage: () => Promise<void>
  previousPage: () => Promise<void>
  reset: () => void
}

const EMPTY_FILTERS = (): InsightConfigBrowserFilters => ({
  search: '',
  status: '',
  platform: '',
  outcome: '',
  severity: '',
})

export function useInsightConfigBrowser(
  options: UseInsightConfigBrowserOptions,
): UseInsightConfigBrowser {
  const api = options.api ?? insightWorkbenchApi
  const configs = ref<InsightConfig[]>([])
  const total = ref(0)
  const offset = ref(0)
  const state = ref<WorkbenchAsyncState>('idle')
  const error = ref<ApiError | null>(null)
  const draftFilters = ref(EMPTY_FILTERS())
  const appliedFilters = ref(EMPTY_FILTERS())
  let generation = 0
  let controller: AbortController | null = null

  const contextKey = computed(
    () => `${toValue(options.accountId) ?? ''}:${toValue(options.workspaceId) ?? ''}`,
  )
  const enabled = computed(() => (options.enabled === undefined ? true : toValue(options.enabled)))

  function reset(): void {
    generation += 1
    controller?.abort()
    controller = null
    configs.value = []
    total.value = 0
    offset.value = 0
    state.value = 'idle'
    error.value = null
  }

  async function load(): Promise<void> {
    const accountId = toValue(options.accountId)
    const workspaceId = toValue(options.workspaceId)
    if (!enabled.value || accountId === undefined || workspaceId === undefined) {
      reset()
      return
    }

    const request = ++generation
    controller?.abort()
    const activeController = new AbortController()
    controller = activeController
    const requestContext = contextKey.value
    state.value = 'loading'
    error.value = null

    try {
      const filters = appliedFilters.value
      const page = await api.listConfigs(
        accountId,
        workspaceId,
        {
          limit: options.limit ?? 20,
          offset: offset.value,
          search: filters.search,
          status: filters.status,
          platform: normalizeWorkbenchPlatformFilter(filters.platform),
          outcome_category: filters.outcome,
          severity: filters.severity,
        },
        activeController.signal,
      )
      if (
        request !== generation ||
        activeController.signal.aborted ||
        requestContext !== contextKey.value
      ) {
        return
      }
      configs.value = page.results
      total.value = page.count
      state.value = page.results.length > 0 ? 'ready' : 'empty'
    } catch (cause) {
      if (request !== generation || activeController.signal.aborted) return
      const normalized = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
      if (normalized.kind === 'canceled') return
      configs.value = []
      total.value = 0
      error.value = normalized
      state.value = 'error'
    }
  }

  async function applyFilters(): Promise<void> {
    appliedFilters.value = {
      ...draftFilters.value,
      platform: normalizeWorkbenchPlatformFilter(draftFilters.value.platform),
    }
    offset.value = 0
    await load()
  }

  async function clearFilters(): Promise<void> {
    draftFilters.value = EMPTY_FILTERS()
    appliedFilters.value = EMPTY_FILTERS()
    offset.value = 0
    await load()
  }

  async function nextPage(): Promise<void> {
    if (offset.value + (options.limit ?? 20) >= total.value) return
    offset.value += options.limit ?? 20
    await load()
  }

  async function previousPage(): Promise<void> {
    if (offset.value === 0) return
    offset.value = Math.max(0, offset.value - (options.limit ?? 20))
    await load()
  }

  watch(
    [contextKey, enabled],
    () => {
      reset()
      if (enabled.value) void load()
    },
    { immediate: true },
  )
  onScopeDispose(reset)

  return {
    configs: computed(() => configs.value),
    total: computed(() => total.value),
    offset: computed(() => offset.value),
    state: computed(() => state.value),
    error: computed(() => error.value),
    draftFilters,
    appliedFilters: computed(() => appliedFilters.value),
    applyFilters,
    clearFilters,
    load,
    nextPage,
    previousPage,
    reset,
  }
}
